<template>
	<div
		ref="playerOverlay"
		v-measure-width="{
			callback: onOverlayWidthChange,
			sizes: OVERLAY_CONFIG
		}"
		class="player-overlay"
		:class="[
			{
				'interaction-overlay-is-visible': interactionOverlayIsActuallyVisible,
				'tag-timeline-is-visible': overlayUiConfig.tagTimeline.enabled,
				'settings-pop-out-is-visible': isSettingsPopOutVisible,
				'is-mobile-web-app': isMobileWebApp,
				'is-scrubber-dragging': scrubberDragging,
				'is-cinema-mode': isCinemaMode,
				'is-fullscreen': isFullscreen,
				'is-embedded': isEmbedded,
				'live-border-is-visible': overlayUiConfig.liveBorder.enabled,
			},
			`-player-overlay-size-${overlaySize}`,
		]"
		@click="(e) => handleOverlayClick(e)"
		@mousemove="handleMouseMove"
		@mouseleave="handleMouseLeave"
	>
		<CouchModeClose
			v-if="overlayUiConfig.couchModeClose.enabled"
			class="player-overlay__couchmode-close"
			@close-couch-mode="$emit('close-couch-mode')"
		/>

		<BackButton
			v-if="overlayUiConfig.backButton.enabled"
			class="player-overlay__back-button"
			v-bind="backButton"
		/>

		<!-- game information overlay - scoreboard, game clock -->
		<GameInformation
			v-if="overlayUiConfig.gameInformation.enabled"
			class="player-overlay__game-information"
			:style="{ transform: `scale(${scaleFactor1080p})` }"
			v-bind="gameInformation"
		/>

		<PlayerContentInfo
			v-if="overlayUiConfig.playerContentInfo.enabled"
			class="player-overlay__content-info"
			v-bind="playerContentInfo"
		/>

		<ProductPlacementInfo
			v-if="overlayUiConfig.productPlacementInfo.enabled"
			class="player-overlay__product-placement-info"
			:style="{ transform: `scale(${scaleFactor})` }"
			v-bind="productPlacementInfo"
		/>

		<VideoInteractionFeedbackOverlay
			v-if="overlayUiConfig.videoInteractionFeedback.enabled"
			ref="videoInteractionFeedback"
			class="player-overlay__video-interaction-feedback"
			:player-overlay-is-visible="isInteractionOverlayVisible || forceShowInteractionOverlay"
			v-bind="videoInteractionFeedbackOverlay"
			@toggle-play-pause="$emit('touch-toggle-play-pause')"
		/>

		<MonetizationImageRenderer
			v-if="overlayUiConfig.logo.enabled && !takeoverAd?.visible"
			class="player-overlay__logo"
			:type="AdType.PLAYER_LOGO"
			v-bind="monetizationImageRenderer"
			data-testid="player-logo"
		/>
		<MonetizationImageRenderer
			v-if="overlayUiConfig.bannerAd.enabled && !takeoverAd?.visible"
			class="player-overlay__banner"
			data-testid="player-banner"
			:type="AdType.PLAYER_BANNER"
			v-bind="monetizationImageRenderer"
		/>

		<div
			v-if="takeoverClickAreas.length"
			class="player-overlay__takeover-click-stage"
			:style="takeoverClickStageStyle"
		>
			<button
				v-for="area in takeoverClickAreas"
				:key="area.key"
				type="button"
				class="player-overlay__takeover-click-area"
				:style="area.style"
				@click.stop="handleTakeoverClick"
			/>
		</div>

		<SkipAd
			v-if="overlayUiConfig.skipAd.enabled"
			class="player-overlay__skip-ad"
			v-bind="skipAd"
			@skip-ad="() => $emit('skip-ad')"
		/>

		<GameEventOverlay
			v-if="overlayUiConfig.gameEventOverlay.enabled"
			ref="gameEventOverlay"
			class="player-overlay__game-event-overlay"
			v-bind="gameEventOverlay"
		/>

		<TimelineBlock
			v-if="overlayUiConfig.tagTimeline.enabled"
			class="player-overlay__tag-timeline"
			v-bind="timelineBlock"
		/>

		<PlayToggleOverlay
			v-if="overlayUiConfig.playToggleOverlay.enabled"
			class="player-overlay__play-toggle-overlay"
			v-bind="playToggleOverlay"
			@toggle-play-pause="$emit('touch-toggle-play-pause')"
		/>

		<PlayerControls
			v-if="overlayUiConfig.playerControls.enabled && overlayUiConfig.playerControls.position === 'over'"
			class="player-overlay__controls"
			v-bind="playerControls"
			:size="overlaySize"
			v-on="$listeners"
			@settings-pop-out-toggle="handleSettingsPopOutToggle"
		/>

		<!-- <ClipsPreview
			v-if="overlayUiConfig.clipsPreview.enabled"
			v-show="overlaysActive && !scrubberDragging"
			v-bind="clipsPreview"
			@touchstart="(data) => $emit('clips-preview-touchstart', data)"
			@clip:share-click="(data) => $emit('clips-preview-share-click', data)"
		/> -->

		<PaywallPreviewOverlay
			v-if="overlayUiConfig.paywallPreview.enabled"
			v-bind="paywallPreviewOverlay"
			@limit-reached="(data) => $emit('paywall-preview-limit-reached', data)"
			@preview-started="(data) => $emit('paywall-preview-started', data)"
		/>

		<PlayerErrorSnackbar
			v-if="shouldShowPlayerErrorToast"
			class="player-overlay__error-snackbar"
			:message="$I18n.trans('exception.player.error_network_issues')"
			@close="handlePlayerErrorSnackbarClose"
		/>

		<PlayerErrorSnackbar
			v-if="shouldShowStreamEndsEarlySnackbar"
			class="player-overlay__error-snackbar"
			:message="$I18n.trans('exception.player.error_stream_ends_before_event_ends')"
			:closeable="false"
		/>

		<AutoplayMutedIndicator
			v-if="overlayUiConfig.autoplayMutedIndicator.enabled"
			class="player-overlay__autoplay-muted-indicator"
			v-bind="autoplayMutedIndicator"
			@click="$emit('autoplay-muted-indicator-click')"
		/>

		<div
			v-if="overlayUiConfig.customOverlayImage.enabled"
			:style="{ backgroundImage: `url(${customOverlayImage})` }"
			class="player-overlay__custom-overlay-image"
		/>

		<div
			v-if="overlayUiConfig.iosRotateInfo?.enabled"
			class="player-overlay__ios-rotate-info"
			:class="{ 'is-visible': isIosRotateInfoVisible }"
		>
			<Icon
				icon-name="restore-1"
				size="xxl"
			/>
			{{ $I18n.trans('commons.rotate_device') }}
		</div>
	</div>
</template>

<script>
import GameInformation from '@components/player/GameInformation';
import PlayerControls from '@shared/components/PlayerControls/PlayerControls.vue';
import TimelineBlock from '@videotools/js/blocks/Timeline';
import SkipAd from '@videotools/js/components/player/overlays/SkipAd';
import CouchModeClose from '@videotools/js/components/player/overlays/CouchModeClose';
import BackButton from '@videotools/js/components/player/overlays/BackButton';
import PlayerContentInfo from '@components/player/PlayerContentInfo';
import GameEventOverlay from '@components/player/graphics/GameEventOverlay.vue';
// import ClipsPreview from '@components/player/overlays/ClipsPreview';
import VideoInteractionFeedbackOverlay from '@shared/components/PlayerControls/VideoInteractionFeedbackOverlay.vue';
import PlayToggleOverlay from '@shared/components/PlayerControls/PlayToggleOverlay.vue';
import AutoplayMutedIndicator from '@shared/components/AutoplayMutedIndicator.vue';
import ProductPlacementInfo from '@shared/components/ProductPlacementInfo.vue';
import PaywallPreviewOverlay from '@components/player/overlays/PaywallPreviewOverlay.vue';
import MonetizationImageRenderer from '@components/player/monetization/ImageRenderer.vue';
import PlayerErrorSnackbar from '@components/notifications/PlayerErrorSnackbar';
import Icon from '@shared/components/Icon';
import MultiTapHandler from '@videotools/js/plugins/multi-tap-handler';
import VMeasureWidth from '@shared/directives/v-measure-width';
import { OVERLAY_CONFIG } from '@shared/constants/overlaySizes';
import { MOUSE_MOVE_HIDE_THROTTLE_INTERVAL, HIDE_OVERLAY_TIMEOUT } from '@videotools/js/constants/player';
import { AdType } from '@shared/enums/AdType';
import { TakeoverPosition } from '@shared/enums/TakeoverPosition';
import { throttle } from 'lodash-es';

const MOBILE_PLAYER_CONTROLS_BUTTON_WIDTH = 36;

/**
 * Important:
 * This is a dumb component that just renders the overlays.
 * It does not contain any logic for the player.
 * The logic for the player is in the PlayerWrapper component.
 */

export default {
	name:       'PlayerOverlays',
	components: {
		GameInformation,
		VideoInteractionFeedbackOverlay,
		AutoplayMutedIndicator,
		ProductPlacementInfo,
		PaywallPreviewOverlay,
		PlayerControls,
		SkipAd,
		CouchModeClose,
		BackButton,
		PlayerContentInfo,
		GameEventOverlay,
		PlayToggleOverlay,
		// ClipsPreview,
		TimelineBlock,
		MonetizationImageRenderer,
		PlayerErrorSnackbar,
		Icon,
	},
	directives: {
		measureWidth: VMeasureWidth,
	},
	props: {
		// generic props
		overlayUiConfig: {
			type:     Object,
			required: true,
		},
		// custom props
		forceShowInteractionOverlay: {
			type:    Boolean,
			default: false,
		},
		forceDisableInteractionOverlay: {
			type:    Boolean,
			default: false,
		},
		scrubberDragging: {
			type:    Boolean,
			default: false,
		},
		isCinemaMode: {
			type:    Boolean,
			default: false,
		},
		isFullscreen: {
			type:    Boolean,
			default: false,
		},
		isEmbedded: {
			type:    Boolean,
			default: false,
		},
		clickInteractionEnabled: {
			type:    Boolean,
			default: true,
		},
		// component props
		gameInformation: {
			type:     Object,
			required: true,
		},
		backButton: {
			type:     Object,
			required: true,
		},
		playerContentInfo: {
			type:     Object,
			required: true,
		},
		productPlacementInfo: {
			type:     Object,
			required: true,
		},
		videoInteractionFeedbackOverlay: {
			type:     Object,
			required: true,
		},
		playToggleOverlay: {
			type:     Object,
			required: true,
		},
		monetizationImageRenderer: {
			type:     Object,
			required: true,
		},
		skipAd: {
			type:     Object,
			required: true,
		},
		gameEventOverlay: {
			type:    Object,
			default: null,
		},
		autoplayMutedIndicator: {
			type:     Object,
			required: true,
		},
		timelineBlock: {
			type:     Object,
			required: true,
		},
		playerControls: {
			type:     Object,
			required: true,
		},
		// clipsPreview: {
		// 	type:     Object,
		// 	required: true,
		// },
		paywallPreviewOverlay: {
			type:     Object,
			required: true,
		},
		shouldShowPlayerErrorToast: {
			type:    Boolean,
			default: false,
		},
		shouldShowStreamEndsEarlySnackbar: {
			type:    Boolean,
			default: false,
		},
		customOverlayImage: {
			type:    String,
			default: null,
		},
		// device props
		isTouchDevice: {
			type:    Boolean,
			default: false,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
		takeoverAd: {
			type:    Object,
			default: null,
		},
		takeoverStageRect: {
			type:    Object,
			default: null,
		},
	},
	data() {
		return {
			isMouseMoveActiveOrRecentlyActive: null, // null to trigger watch on load
			mouseMoveTimeout:                  null,
			isInteractionOverlayVisible:       false,
			playerMultiTapHandler:             null,
			overlayWidth:                      1080,
			resizeObserver:                    null,
			overlaySize:                       'lg',
			isSettingsPopOutVisible:           false,
			AdType,
			OVERLAY_CONFIG,
			isIosRotateInfoVisible:            false,
		};
	},
	computed: {
		// Based on 720p -> Use for Components designed for 720p
		scaleFactor() {
			return this.overlayWidth / 1280;
		},
		// Based on 1080p -> Use for Components designed for 1080p
		scaleFactor1080p() {
			return this.overlayWidth / 1920;
		},
		hasHistory() {
			return history.length > 1;
		},
		interactionOverlayIsActuallyVisible() {
			return this.overlayUiConfig.interactionOverlay.enabled && (this.isInteractionOverlayVisible || this.forceShowInteractionOverlay) && !this.forceDisableInteractionOverlay;
		},
		takeoverClickStageStyle() {
			if (!this.takeoverStageRect) {
				return {};
			}

			return {
				top:    `${this.takeoverStageRect.top}px`,
				left:   `${this.takeoverStageRect.left}px`,
				width:  `${this.takeoverStageRect.width}px`,
				height: `${this.takeoverStageRect.height}px`,
			};
		},
		takeoverClickAreas() {
			if (!this.takeoverAd?.visible || !this.takeoverAd.link) {
				return [];
			}

			const scale = Math.max(60, Math.min(Number(this.takeoverAd.playerScalePercent) || 80, 100));
			const inset = 100 - scale;
			const position = this.takeoverAd.takeoverPosition || TakeoverPosition.BOTTOM_LEFT;
			const areas = [];

			if (position.includes('top')) {
				areas.push({
					key:   'top',
					style: { top: 0, left: 0, width: '100%', height: `${inset}%` },
				});
			} else {
				areas.push({
					key:   'bottom',
					style: { bottom: 0, left: 0, width: '100%', height: `${inset}%` },
				});
			}

			if (position.includes('left')) {
				areas.push({
					key:   'left',
					style: { top: position.includes('top') ? `${inset}%` : 0, left: 0, width: `${inset}%`, height: `${scale}%` },
				});
			} else {
				areas.push({
					key:   'right',
					style: { top: position.includes('top') ? `${inset}%` : 0, right: 0, width: `${inset}%`, height: `${scale}%` },
				});
			}

			return areas;
		},
	},
	watch: {
		isMouseMoveActiveOrRecentlyActive: {
			handler(newVal) {
				if (newVal) {
					this.showInteractionOverlay();
				} else {
					this.hideInteractionOverlay();
				}
			},
		},
		isInteractionOverlayVisible: {
			handler(newVal) {
				this.$emit('interaction-overlay-visibility-change', newVal);
			},
		},
	},
	mounted() {
		this.initMultiTapHandler();
		this.initResizeObserver();
		// when component remounts (e.g. on skip ad), we need to emit the current state
		this.$emit('interaction-overlay-visibility-change', this.isInteractionOverlayVisible);
	},
	destroyed() {
		clearTimeout(this.playerOverlayTimeout);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	},
	methods: {
		onOverlayWidthChange(newSize) {
			this.overlaySize = newSize;
		},
		handleOverlayClick(e) {
			if (!this.clickInteractionEnabled) {
				return;
			}

			if (this.isTouchDevice) {
				this.handleTapping(e);
			} else {
				this.$emit('overlay-click');
			}
		},
		handleTakeoverClick() {
			if (this.takeoverAd?.link) {
				window.open(this.takeoverAd.link, '_blank');
			}
		},
		handleMouseMove: throttle(
			function() {
				if (this.isTouchDevice) {
					return;
				}

				clearTimeout(this.mouseMoveTimeout);
				this.isMouseMoveActiveOrRecentlyActive = true;

				this.mouseMoveTimeout = setTimeout(() => {
					this.isMouseMoveActiveOrRecentlyActive = false;
				}, HIDE_OVERLAY_TIMEOUT);

				this.showInteractionOverlay();
				this.autoHidePlayerControls();
			},
			MOUSE_MOVE_HIDE_THROTTLE_INTERVAL,
			{ trailing: false }
		),
		handleMouseLeave() {
			clearTimeout(this.mouseMoveTimeout);
			this.isMouseMoveActiveOrRecentlyActive = false;
		},
		initMultiTapHandler() {
			const handlers = {
				onSingleTap: () => {
					this.$emit('single-tap');
				},
				onDoubleTap: (e) => {
					this.$emit('double-tap', this.getTapDirectionFromEvent(e));
				},
				onTripleTap: (e) => {
					this.$emit('triple-tap', this.getTapDirectionFromEvent(e));
				},
			};

			this.playerMultiTapHandler = new MultiTapHandler(handlers);
		},
		handleTapping(e) {
			if (this.playerMultiTapHandler) {
				this.playerMultiTapHandler.handle(e);
			}
		},
		getTapDirectionFromEvent(e) {
			let dir;
			const width = e.target.clientWidth;
			const x = e.clientX;
			const leftLimit = width / 2 - MOBILE_PLAYER_CONTROLS_BUTTON_WIDTH / 2;
			const rightLimit = width / 2 + MOBILE_PLAYER_CONTROLS_BUTTON_WIDTH / 2;

			if (x <= leftLimit) {
				dir = 'backward';
			}

			if (x >= rightLimit) {
				dir = 'forward';
			}

			return dir;
		},
		showInteractionOverlay() {
			if (this.isInteractionOverlayVisible) {
				return;
			}
			this.isInteractionOverlayVisible = true;
		},
		hideInteractionOverlay() {
			if (!this.isInteractionOverlayVisible) {
				return;
			}
			this.isInteractionOverlayVisible = false;
		},
		toggleInteractionOverlay() {
			this.isInteractionOverlayVisible = !this.isInteractionOverlayVisible;
		},
		autoHidePlayerControls() {
			this.clearAutoHidePlayerControlsTimeout();

			// todo controls1 landscape tag preview / ClipsPreview
			// if (this.timelineTagPreviewOpened) return;

			this.playerOverlayTimeout = setTimeout(() => {
				this.hideInteractionOverlay();
			}, HIDE_OVERLAY_TIMEOUT);
		},
		clearAutoHidePlayerControlsTimeout() {
			if (this.playerOverlayTimeout) {
				clearTimeout(this.playerOverlayTimeout);
				this.playerOverlayTimeout = null;
			}
		},
		// videoInteractionFeedback
		handleBackward(data) {
			this.$refs.videoInteractionFeedback?.handleBackward(data);
		},
		handleForward(data) {
			this.$refs.videoInteractionFeedback?.handleForward(data);
		},
		handlePlay(data) {
			this.$refs.videoInteractionFeedback?.handlePlay(data);
		},
		handlePause(data) {
			this.$refs.videoInteractionFeedback?.handlePause(data);
		},
		handlePlayerErrorSnackbarClose() {
			this.$emit('cancel-error-toast');
		},

		// gameEventOverlay
		handleGameEventOverlayReset() {
			this.$refs.gameEventOverlay?.reset();
		},
		handleGameEventOverlayPause() {
			this.$refs.gameEventOverlay?.pause();
		},
		initResizeObserver() {
			this.resizeObserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					if (entry.target === this.$refs.playerOverlay) {
						this.overlayWidth = entry.contentRect.width;
					}
				}
			});

			if (this.$refs.playerOverlay) {
				this.resizeObserver.observe(this.$refs.playerOverlay);
			}
		},
		handleSettingsPopOutToggle(value) {
			this.isSettingsPopOutVisible = value;
		},
		showIosRotateInfo() {
			this.isIosRotateInfoVisible = true;

			setTimeout(() => {
				this.isIosRotateInfoVisible = false;
			}, 1400);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.player-overlay {
	--player-overlay-top-offset: 4%;
	--player-overlay-h-offset: 2.5%;

	@include z-index('full-page-overlays', 'global');
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	touch-action: manipulation;

	&::after {
		@include z-index('bottom-shadow', 'full-page-overlays');
		content: '';
		position: absolute;
		bottom: -1px;
		width: 100%;
		height: 35%;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0.9) 14.42%, rgba(0, 0, 0, 0) 100%);
		opacity: 0;
		transform: rotate(-180deg);
		transition: opacity $player-overlay-animation;
		pointer-events: none;
	}

	&.is-mobile-web-app {
		&::after {
			height: 100%;
			background: var(--color-black);
		}
	}

	&.interaction-overlay-is-visible {
		&::after {
			opacity: 0.8;
		}

		&.is-mobile-web-app {
			&::after {
				opacity: 0.6;
			}
		}
	}

	&.is-scrubber-dragging {
		&::after {
			opacity: 0 !important; // stylelint-disable-line declaration-no-important
		}
	}

	&.live-border-is-visible {
		border: 1px solid var(--color-red);
		border-radius: 4px;
	}
}

.player-overlay__play-toggle-overlay {
	@include z-index('play-toggle-overlay', 'full-page-overlays');

	position: absolute;
	top: 50%;
	left: 0;
	transform: translateY(-50%);
	width: 100%;
	opacity: 0;
	transition: opacity $player-overlay-animation;
	pointer-events: none;

	.player-overlay.interaction-overlay-is-visible & {
		opacity: 1;
		pointer-events: auto;
	}

	.player-overlay.is-scrubber-dragging & {
		opacity: 0;
	}
}

.player-overlay__controls {
	@include z-index('controls', 'full-page-overlays');

	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;
	display: flex;
	justify-content: space-between;
	opacity: 0;
	transition: opacity $player-overlay-animation;

	.player-overlay.interaction-overlay-is-visible & {
		opacity: 1;
	}

	.player-overlay.settings-pop-out-is-visible & {
		@include z-index('controls-with-settings-pop-out', 'full-page-overlays');
	}
}

.player-overlay__tag-timeline {
	@include z-index('tag-timeline', 'full-page-overlays');

	position: absolute;
	bottom: 60px;
	left: 0;
	width: 100%;
	opacity: 0;
	transition: opacity $player-overlay-animation;
	pointer-events: none;

	.player-overlay.is-cinema-mode &,
	.player-overlay.is-fullscreen &,
	.player-overlay.is-embedded & {
		padding-left: var(--controls-h-padding);
		padding-right: var(--controls-h-padding);
	}

	.player-overlay.interaction-overlay-is-visible & {
		opacity: 1;
		pointer-events: auto;
	}
}

.player-overlay__autoplay-muted-indicator {
	@include z-index('autoplay-muted-indicator', 'full-page-overlays');

	position: absolute;
	left: var(--player-overlay-h-offset);
	bottom: 6%;
	transition: bottom $player-overlay-animation;

	// offsetting for controls
	.player-overlay.interaction-overlay-is-visible & {
		bottom: 70px;
	}
	.player-overlay.interaction-overlay-is-visible.-player-overlay-size-md & {
		bottom: 80px;
	}
	.player-overlay.interaction-overlay-is-visible.-player-overlay-size-lg & {
		bottom: 90px;
	}

	// tag timeline is only placed over the player in cinema mode on controls size lg
	.player-overlay.interaction-overlay-is-visible.tag-timeline-is-visible & {
		bottom: 160px;
	}
}

.player-overlay__couchmode-close {
	@include z-index('couch-mode-close', 'full-page-overlays');
	position: relative;
}

.player-overlay__back-button {
	@include z-index('back-button', 'full-page-overlays');

	position: absolute;
	top: 0;
	left: 0;
	padding: var(--controls-top-v-padding) var(--controls-h-padding-mobile-web-app);
	opacity: 0;
	transition: opacity $player-overlay-animation;
	pointer-events: none;

	.player-overlay.interaction-overlay-is-visible & {
		opacity: 1;
		pointer-events: auto;
	}

	.player-overlay.is-scrubber-dragging & {
		opacity: 0 !important; // stylelint-disable-line declaration-no-important
	}
}

.player-overlay__game-information {
	@include z-index('game-information', 'full-page-overlays');

	position: absolute;
	top: var(--player-overlay-top-offset);
	left: var(--player-overlay-h-offset);
	transform-origin: top left;
}

.player-overlay__controls-wrapper {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
}

.player-overlay__game-event-overlay {
	@include z-index('game-event-overlay', 'full-page-overlays');

	position: absolute;
	bottom: 10%;
	left: var(--player-overlay-h-offset);
}

.player-overlay__banner {
	@include z-index('ad-banner', 'full-page-overlays');
	position: absolute;
	bottom: 6%;
	left: 0;
	width: 100%;
	transition: bottom $player-overlay-animation;

	// offsetting for controls
	.player-overlay.interaction-overlay-is-visible & {
		bottom: 70px;
	}
	.player-overlay.interaction-overlay-is-visible.-player-overlay-size-md & {
		bottom: 80px;
	}
	.player-overlay.interaction-overlay-is-visible.-player-overlay-size-lg & {
		bottom: 90px;
	}

	// tag timeline is only placed over the player in cinema mode on controls size lg
	.player-overlay.interaction-overlay-is-visible.tag-timeline-is-visible & {
		bottom: 160px;
	}
}

.player-overlay__takeover-click-area {
	@include z-index('ad-banner', 'full-page-overlays');
	position: absolute;
	padding: 0;
	border: 0;
	background-color: transparent;
	cursor: pointer;
}

.player-overlay__takeover-click-stage {
	@include z-index('ad-banner', 'full-page-overlays');
	position: absolute;
}

.player-overlay__skip-ad {
	@include z-index('skip-ad', 'full-page-overlays');
}

.player-overlay__content-info {
	@include z-index('content-info', 'full-page-overlays');

	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	opacity: 0;
	transition: opacity $player-overlay-animation;
	pointer-events: none;

	.player-overlay.interaction-overlay-is-visible & {
		opacity: 1;
	}
}

.player-overlay__video-interaction-feedback {
	@include z-index('video-interaction-feedback', 'full-page-overlays');

	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	padding: 0 20%;
}

.player-overlay__product-placement-info {
	@include z-index('product-placement-info', 'full-page-overlays');

	position: absolute;
	top: var(--player-overlay-top-offset);
	right: var(--player-overlay-h-offset);
	transform-origin: top right;
}

.player-overlay__logo {
	@include z-index('logo', 'full-page-overlays');

	position: absolute;
	top: var(--player-overlay-top-offset);
	right: var(--player-overlay-h-offset);
}

.player-overlay__error-snackbar {
	@include z-index('error-snackbar', 'full-page-overlays');

	position: absolute;
	top: var(--player-overlay-top-offset);
	right: var(--player-overlay-h-offset);
}

.player-overlay__custom-overlay-image {
	@include z-index('custom-overlay-image', 'full-page-overlays');

	position: absolute;
	top: 0;
	left: 50%;
	width: auto;
	height: 100%;
	background-size: cover;
	aspect-ratio: 16 / 9;
    transform: translateX(-50%);
}

.player-overlay__ios-rotate-info {
	@include z-index('ios-rotate-info', 'full-page-overlays');
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: $sp4;
	justify-content: center;
	align-items: center;
	background-color: rgba(var(--color-rgb-black), 0.8);
	color: var(--color-white);
	font-size: 12px;
	font-weight: 600;
	pointer-events: none;
	opacity: 0;
	transition: opacity $player-overlay-animation;

	&.is-visible {
		opacity: 1;
	}
}
</style>
